import React from "react";
import { Tag, Button } from "antd";
import { CloseCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { type AseoFilterValues, initialAseoFilterValues } from "./AseoFilterBar";

// Etiquetas legibles para los códigos internos de estado/urgencia. Sin esto,
// el chip mostraría "en_proceso" o "am" crudos en vez de un texto amigable.
const ESTADO_LABELS: Record<string, string> = {
  pendiente: "Pendiente",
  en_proceso: "En proceso",
  completado: "Completado",
  pagado: "Pagado",
  cancelado: "Cancelado",
};
const URGENCIA_LABELS: Record<string, string> = {
  am: "AM",
  pm: "PM",
  "critico 11:00": "Crítico 11:00",
  "11:00": "11:00",
};

// Nombres de mes en español (sin depender del locale global de dayjs) para
// rotular el chip cuando el rango de fecha es exactamente un mes calendario
// (caso del atajo "Mes" de la barra de filtros rápidos).
const MESES_ES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

// Campos del filtro que son arrays de strings (aseador, propiedad, etc.).
// Se tipa explícito para que el remove individual sea type-safe.
type ArrayFilterKey =
  | "aseadores"
  | "propiedad"
  | "sector"
  | "estado"
  | "urgencia";

interface Chip {
  key: string;
  label: string;
  onClose: () => void;
}

interface Props {
  filterValues: AseoFilterValues;
  setFilterValues: React.Dispatch<React.SetStateAction<AseoFilterValues>>;
}

/**
 * Barra de "filtros activos": muestra cada filtro aplicado como un chip
 * removible y un botón "Limpiar todo". Antes los filtros vivían SOLO dentro
 * del acordeón "Filtros avanzados" (colapsado por defecto), así que al
 * redirigir con un filtro pre-aplicado el admin no veía qué estaba filtrando
 * ni cómo quitarlo. Esta barra hace visible y reversible el estado del filtro
 * sin obligar a abrir el panel. Round 2026-06-23.
 */
const ActiveAseoFilters: React.FC<Props> = ({
  filterValues,
  setFilterValues,
}) => {
  const f = filterValues;
  const chips: Chip[] = [];

  // Quita un valor puntual de un filtro tipo array (deja el resto intactos).
  const removeFromArray = (field: ArrayFilterKey, value: string) =>
    setFilterValues((prev) => ({
      ...prev,
      [field]: (prev[field] as string[]).filter((v) => v !== value),
    }));

  f.aseadores.forEach((v) =>
    chips.push({
      key: `ase-${v}`,
      label: `Aseador: ${v}`,
      onClose: () => removeFromArray("aseadores", v),
    }),
  );
  f.propiedad.forEach((v) =>
    chips.push({
      key: `prop-${v}`,
      label: `Propiedad: ${v}`,
      onClose: () => removeFromArray("propiedad", v),
    }),
  );
  f.sector.forEach((v) =>
    chips.push({
      key: `sec-${v}`,
      label: `Sector: ${v}`,
      onClose: () => removeFromArray("sector", v),
    }),
  );
  f.estado.forEach((v) =>
    chips.push({
      key: `est-${v}`,
      label: `Estado: ${ESTADO_LABELS[v] ?? v}`,
      onClose: () => removeFromArray("estado", v),
    }),
  );
  f.urgencia.forEach((v) =>
    chips.push({
      key: `urg-${v}`,
      label: `Urgencia: ${URGENCIA_LABELS[v] ?? v}`,
      onClose: () => removeFromArray("urgencia", v),
    }),
  );

  if (f.fechaInicio[0] && f.fechaInicio[1]) {
    const start = dayjs(f.fechaInicio[0]);
    const end = dayjs(f.fechaInicio[1]);
    // Si el rango cubre exactamente un mes calendario, lo mostramos como
    // "Mes: Julio 2026" (más legible que el rango completo). Es el caso del
    // atajo "Mes" de la barra rápida.
    const esMesCompleto =
      start.isValid() &&
      end.isValid() &&
      start.isSame(start.startOf("month"), "day") &&
      end.isSame(start.endOf("month"), "day");
    chips.push({
      key: "fecha",
      label: esMesCompleto
        ? `Mes: ${MESES_ES[start.month()]} ${start.year()}`
        : `Fecha: ${start.format("DD/MM/YY")} → ${end.format("DD/MM/YY")}`,
      onClose: () =>
        setFilterValues((p) => ({ ...p, fechaInicio: [null, null] })),
    });
  }
  if (f.horario[0] && f.horario[1]) {
    chips.push({
      key: "hora",
      label: `Hora: ${f.horario[0]} → ${f.horario[1]}`,
      onClose: () => setFilterValues((p) => ({ ...p, horario: [null, null] })),
    });
  }
  if (f.sueldoAseador[0] != null || f.sueldoAseador[1] != null) {
    chips.push({
      key: "sueldo",
      label: `Sueldo: ${f.sueldoAseador[0] ?? "0"} – ${
        f.sueldoAseador[1] ?? "∞"
      }`,
      onClose: () =>
        setFilterValues((p) => ({ ...p, sueldoAseador: [null, null] })),
    });
  }
  if (f.costoLimpieza[0] != null || f.costoLimpieza[1] != null) {
    chips.push({
      key: "costo",
      label: `Costo: ${f.costoLimpieza[0] ?? "0"} – ${
        f.costoLimpieza[1] ?? "∞"
      }`,
      onClose: () =>
        setFilterValues((p) => ({ ...p, costoLimpieza: [null, null] })),
    });
  }
  if (f.observaciones && f.observaciones.trim()) {
    chips.push({
      key: "obs",
      label: `Notas: "${f.observaciones.trim()}"`,
      onClose: () => setFilterValues((p) => ({ ...p, observaciones: "" })),
    });
  }

  // Sin filtros activos no renderizamos nada (evita ocupar espacio en vano).
  if (chips.length === 0) return null;

  return (
    <div
      style={{
        marginBottom: 12,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span style={{ fontWeight: 600, marginRight: 4 }}>Filtros activos:</span>
      {chips.map((c) => (
        <Tag
          key={c.key}
          closable
          color="blue"
          style={{ marginInlineEnd: 0 }}
          // preventDefault evita la animación interna de AntD que oculta el
          // tag por su cuenta: el render lo controla nuestro estado.
          onClose={(e) => {
            e.preventDefault();
            c.onClose();
          }}
        >
          {c.label}
        </Tag>
      ))}
      <Button
        size="small"
        type="link"
        danger
        icon={<CloseCircleOutlined />}
        onClick={() => setFilterValues(initialAseoFilterValues)}
      >
        Limpiar todo
      </Button>
    </div>
  );
};

export default ActiveAseoFilters;
